import { request } from './core';
import * as ImportSessionTypes from '@/@types/importSessionTypes';
import * as BusinessTypes from '@/@types/businessTypes';

/**
 * Fetches Rhapsody businesses based on the search params
 * @param {Object} FetchBusinessesRequestParams
 * @param {string} FetchBusinessesRequestParams.search
 * @param {number=} FetchBusinessesRequestParams.from
 * @param {number=} FetchBusinessesRequestParams.to
 * @param {string} FetchBusinessesRequestParams.environment
 * @returns {Promise<RhapsodyBusinessesPaginatedResponse>}
 */
export const fetchBusinesses = ({ search, from = 0, to = 20, environment }) => {
  return request('businesses', {
    method: 'GET',
    params: {
      search,
      from,
      to,
      environment
    }
  });
};

/**
 * Returns all V3 Import Sessions associated with the business id
 * @param {string} businessId
 * @param {string} environment
 * @returns {Promise<ImportSessionTypes.ImportSessionV3[]>}
 */
export const fetchBusinessSessions = (businessId, environment) => {
  return request('businesses/sessions', {
    method: 'GET',
    params: {
      businessId,
      environment
    }
  });
};

/**
 * Fetches the specified V3 Import Session
 * @param {string} businessId
 * @param {string} environment
 * @returns {Promise<ImportSessionTypes.ImportSessionV3>}
 */
export const fetchBusinessSession = (sessionId, environment) => {
  return request('businesses/session', {
    method: 'GET',
    params: {
      sessionId,
      environment
    }
  });
};

/**
 * Fetches the source and destination data locations for a given import request
 * @param {string} requestId
 * @param {string} environment
 * @returns {Promise<{ sourceDataLocation: string=; destinationDataLocation: string=}>}
 */
export const fetchBusinessImportRequestSourceDataLocation = (
  requestId,
  environment
) => {
  return request('businesses/request-source-data-location', {
    method: 'GET',
    params: {
      requestId,
      environment
    }
  });
};

/**
 * Fetches a list of the supported session static files and flags to indicate whether they exist
 * @param {string} sessionId
 * @param {string} environment
 * @returns {Promise<[{name: string, extension: string, exists: boolean}]>} List of supported static files
 */
export const fetchBusinessSessionFiles = (sessionId, environment) => {
  return request('businesses/session-files', {
    method: 'GET',
    params: {
      sessionId,
      environment
    }
  });
};

/**
 * Returns the list of sites available for a group clinic. Used when working with grouped clinics that
 * store multiple practices within one dataset and we need to distingish a single practice's data
 * @param {string} environment
 * @param {string} sessionId
 * @returns {Promise<[{site: string, name: string, available: boolean }]>}
 */
export const fetchSessionSites = (environment, sessionId) => {
  return request('businesses/sessions/sites', {
    method: 'GET',
    params: {
      environment,
      sessionId
    }
  });
};

/**
 * Fetches the settings for a given session id
 * @param {string} environment
 * @param {string} sessionId
 * @returns {Promise<ImportSessionTypes.ImportSessionSettingsV3>}
 */
export const fetchSessionSettings = (environment, sessionId) => {
  return request('businesses/sessions/settings', {
    method: 'GET',
    params: {
      environment,
      sessionId
    }
  });
};

/**
 * Used to update the site for a given group import session
 * @param {string} environment
 * @param {string} sessionId
 * @param {string} site
 * @returns {Promise<string>} A string indicating the site has been updated
 */
export const updateSessionSite = (environment, sessionId, site) => {
  return request('businesses/sessions/sites', {
    method: 'POST',
    data: {
      environment,
      sessionId,
      site
    }
  });
};

/**
 * Updates the specified settings for the session
 * @param {string} environment
 * @param {string} sessionId
 * @param {Partial<ImportSessionTypes.ImportSessionSettingsV3>} settings
 * @returns {Promise<ImportSessionTypes.ImportSessionSettingsV3} The updated session settings
 */
export const updateSessionSettings = (environment, sessionId, settings) => {
  return request('businesses/sessions/settings', {
    method: 'PUT',
    params: {
      environment,
      sessionId
    },
    data: {
      settings
    }
  });
};

/**
 * @typedef {Object} FetchImportRequestsPaginatedResponse
 * @property {BusinessTypes.RhapsodyBusiness[]} data
 * @property {number} totalCount
 */

/**
 * Fetches the import requests for a specified import session
 * @param {Object} FetchImportRequestsParams
 * @param {string} FetchImportRequestsParams.sessionId
 * @param {number} FetchImportRequestsParams.from
 * @param {number} FetchImportRequestsParams.to
 * @param {string} FetchImportRequestsParams.environment
 * @param {string[]=} FetchImportRequestsParams.types
 * @returns {Promise<FetchImportRequestsPaginatedResponse>}
 */
export const fetchSessionImportRequests = ({
  sessionId,
  from = 0,
  to = 50,
  environment,
  types
}) => {
  // Use native request params builder to support serialization of types array
  const requestParams = new URLSearchParams();
  requestParams.append('sessionId', sessionId);
  requestParams.append('from', from);
  requestParams.append('to', to);
  requestParams.append('environment', environment);
  requestParams.append('types', types);

  return request(`businesses/sessions/requests?${requestParams.toString()}`);
};

/**
 * Fetches the specified import request
 * @param {string} importRequestId
 * @param {string} environment
 * @returns {Promise<ImportSessionTypes.ImportRequestV3>}
 */
export const fetchSessionImportRequest = (importRequestId, environment) => {
  return request('businesses/sessions/request', {
    params: {
      importRequestId,
      environment
    }
  });
};

/**
 * Fetches the businesses saved to the Matcher DB. Typically used for searching Matcher tasks by business
 * @returns {Promise<BusinessTypes.SavedBusiness>}
 */
export const fetchSavedBusinesses = () => request('businesses/saved');
